import { Alert, Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Button, Stack, Typography } from '@mui/material';
import { memo, useCallback } from 'react';
import { CURRENT_PLAN_BANNER_ID } from '../current-plan-banner/config';
import { useCurrentPlan } from '../current-plan-banner/hooks/use-current-plan';
import { ALERT_COPY } from './config';
import * as SX from './sx';
import type { CurrentPlanAlertProps as Props } from './types';
import { buildMessage } from './utils';

export const CurrentPlanAlert = memo(({ className }: Props) => {
  const { data: plan } = useCurrentPlan();

  const handleScroll = useCallback(() => {
    document
      .getElementById(CURRENT_PLAN_BANNER_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  if (!plan) return null;

  const copy = ALERT_COPY[plan.variant];
  const message = buildMessage(
    plan.variant,
    plan.name,
    plan.requestCount,
    plan.price
  );

  return (
    <Alert
      className={classnames('CurrentPlanAlert', className)}
      severity={copy.severity}
      variant="standard"
      icon={false}
      sx={SX.root}
      actionButton={
        <Button
          variant="contained"
          color={copy.severity === 'warning' ? 'warning' : 'primary'}
          size="small"
          endIcon={<Icon name="arrow-downward-outline" size={16} />}
          onClick={handleScroll}
          sx={SX.button}
        >
          View plan details
        </Button>
      }
    >
      <Stack gap={0.25}>
        <Typography
          variant="body3"
          fontWeight={700}
          color={`${copy.severity}.main`}
        >
          {copy.title}
        </Typography>
        <Typography variant="body4" color={`${copy.severity}.main`}>
          {message}
        </Typography>
      </Stack>
    </Alert>
  );
});

CurrentPlanAlert.displayName = 'CurrentPlanAlert';
